import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ConsultationSource } from './ConsultationSource';
import { ConsultationSourceFilter } from './ConsultationSourceFilter';
import { takeAdminParam } from '../../lib/adminLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import { Calendar, Users, Check, X, Trash2, Download, Eye, ChevronDown, ChevronUp, Mail, Phone, MapPin, RefreshCw, Search } from 'lucide-react';

const ConsultationsTab = ({ token, user }) => {
  const [consultations, setConsultations] = useState([]);
  const [focusId] = useState(() => takeAdminParam('consultation'));
  useEffect(() => {
    if (focusId && consultations.some((c) => c.id === focusId)) document.getElementById(`consultation-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [focusId, consultations]);
  const [deletedConsultations, setDeletedConsultations] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [selectedInstaller, setSelectedInstaller] = useState('');
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConsultationData, setDeleteConsultationData] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'deleted'
  const [isRestoring, setIsRestoring] = useState(false);
  const [filters, setFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const filtersRef = useRef(filters);
  const changeFilters = (f) => { filtersRef.current = f; setFilters(f); setSelectedIds([]); fetchConsultations(); };

  const isSuperAdmin = user?.email === 'admin@test.com' || 
                       user?.email === 'admin@apolloshades.com' || 
                       user?.is_super_admin || 
                       user?.role_id === 'role_super_admin' ||
                       user?.role === 'admin';

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM'
  ];

  useEffect(() => {
    fetchConsultations();
    fetchDeletedConsultations();
    fetchInstallers();
  }, []);

  const fetchConsultations = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filtersRef.current).filter(([, v]) => v));
      const response = await axios.get(`${API_URL}/admin/consultations`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setConsultations(response.data);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedConsultations = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/consultations/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch deleted consultations:', error);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch installers:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(consultations.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    
    // Check if any are completed - require reason
    const hasCompleted = consultations.filter(c => selectedIds.includes(c.id)).some(c => c.status === 'completed');
    if (hasCompleted && !bulkDeleteReason.trim()) {
      toast.error('Please provide a reason for deleting completed consultations');
      return;
    }
    
    setIsDeleting(true);
    try {
      await axios.post(`${API_URL}/admin/consultations/bulk-delete`, {
        ids: selectedIds,
        reason: bulkDeleteReason || 'Bulk deletion'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${selectedIds.length} consultations`);
      setShowBulkDeleteModal(false);
      setSelectedIds([]);
      setBulkDeleteReason('');
      fetchConsultations();
      fetchDeletedConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete consultations');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (id) => {
    setIsRestoring(true);
    try {
      await axios.post(`${API_URL}/admin/consultations/${id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consultation restored');
      fetchConsultations();
      fetchDeletedConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore consultation');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/consultations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consultations_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export consultations');
    }
  };

  const openConfirmModal = (consultation) => {
    setSelectedConsultation(consultation);
    setSelectedInstaller('');
    // Pre-fill date and time from consultation request
    setConfirmDate(consultation.preferred_date || '');
    setConfirmTime(consultation.preferred_time || '10:00 AM');
    setShowConfirmModal(true);
  };

  const confirmConsultation = async () => {
    if (!selectedConsultation) return;
    if (!confirmDate) {
      toast.error('Please select a date for the consultation');
      return;
    }
    
    setIsConfirming(true);
    try {
      await axios.patch(`${API_URL}/admin/consultations/${selectedConsultation.id}`, { 
        status: 'confirmed',
        installer_id: selectedInstaller || null,
        preferred_date: confirmDate,
        preferred_time: confirmTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consultation confirmed! Calendar event created and emails queued.');
      setShowConfirmModal(false);
      setSelectedConsultation(null);
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to confirm consultation');
    } finally {
      setIsConfirming(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/admin/consultations/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteConsultationFn = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the consultation for "${name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/admin/consultations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consultation deleted');
      fetchConsultations();
    } catch (error) {
      toast.error('Failed to delete consultation');
    }
  };

  const openDeleteModal = (consultation) => {
    setDeleteConsultationData(consultation);
    setDeleteReason('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteConsultationData) return;
    
    // For completed consultations, require a reason
    if (deleteConsultationData.status === 'completed' && !deleteReason.trim()) {
      toast.error('Please provide a reason for deleting a completed consultation');
      return;
    }
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/consultations/${deleteConsultationData.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { 
          reason: deleteReason,
          status: deleteConsultationData.status
        }
      });
      toast.success('Consultation deleted');
      setShowDeleteModal(false);
      setDeleteConsultationData(null);
      setDeleteReason('');
      fetchConsultations();
      fetchDeletedConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete consultation');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-neutral-100 text-neutral-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-700';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Consultation Requests</h2>
          <p className="text-neutral-500 text-sm">Manage incoming consultation bookings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Tabs for Active vs Deleted */}
      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'active' ? 'border-amber-500 text-amber-600' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
        >
          Active ({consultations.length})
        </button>
        <button
          onClick={() => setActiveTab('deleted')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'deleted' ? 'border-red-500 text-red-600' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
        >
          Deleted ({deletedConsultations.length})
        </button>
      </div>

      {activeTab === 'active' && <ConsultationSourceFilter token={token} filters={filters} onChange={changeFilters} refreshKey={refreshKey} />}

      {/* Bulk Actions Bar */}
      {activeTab === 'active' && selectedIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-amber-800">{selectedIds.length} consultation(s) selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
              Clear Selection
            </Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setShowBulkDeleteModal(true)}>
              <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : activeTab === 'active' ? (
        /* Active Consultations Tab */
        consultations.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg">
            <Calendar className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
            <p className="text-neutral-600">{Object.values(filters).some(Boolean) ? 'No consultations match these filters' : 'No consultation requests yet'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2 pb-2 border-b">
              <input
                type="checkbox"
                checked={selectedIds.length === consultations.length && consultations.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-neutral-300"
              />
              <span className="text-sm text-neutral-500">Select all</span>
            </div>
            
            {consultations.map((consultation) => (
              <div key={consultation.id} id={`consultation-${consultation.id}`} data-testid={`consultation-row-${consultation.id}`}
                className={`border rounded-lg p-4 ${consultation.id === focusId ? 'border-amber-400 ring-2 ring-amber-200' : 'border-neutral-200'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(consultation.id)}
                    onChange={() => handleSelectOne(consultation.id)}
                    className="w-4 h-4 rounded border-neutral-300 mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium">{consultation.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(consultation.status)}`}>
                            {consultation.status}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-600">{consultation.email} • {consultation.phone}</p>
                        {consultation.preferred_date && (
                          <p className="text-sm text-neutral-500 mt-1">
                            Preferred: {consultation.preferred_date} {consultation.preferred_time && `at ${consultation.preferred_time}`}
                          </p>
                        )}
                        <p className="text-sm text-neutral-500 capitalize">Project: {consultation.project_type}</p>
                        <ConsultationSource c={consultation} />
                        {consultation.message && (
                          <p className="text-sm text-neutral-600 mt-2 bg-neutral-50 p-2 rounded">{consultation.message}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {consultation.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" onClick={() => openConfirmModal(consultation)}>
                              <Check className="w-4 h-4 mr-1" /> Confirm
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => updateStatus(consultation.id, 'cancelled')}>
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {consultation.status === 'confirmed' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => updateStatus(consultation.id, 'completed')}>
                              Mark Complete
                            </Button>
                            {isSuperAdmin && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => openDeleteModal(consultation)}
                                title="Super Admin: Delete confirmed consultation"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {consultation.status === 'completed' && isSuperAdmin && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => openDeleteModal(consultation)}
                            title="Super Admin: Delete completed consultation (requires reason)"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        )}
                        {consultation.status === 'cancelled' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => openDeleteModal(consultation)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* Deleted Consultations Tab (Audit Log) */
        deletedConsultations.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg">
            <Trash2 className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
            <p className="text-neutral-600">No deleted consultations</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">
                <strong>Deletion Audit Log</strong> - These consultations have been soft-deleted and can be restored.
              </p>
            </div>
            {deletedConsultations.map((consultation) => (
              <div key={consultation.id} className="border border-red-200 bg-red-50/30 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-neutral-700">{consultation.name}</h4>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Deleted
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(consultation.status_before_deletion || consultation.status)}`}>
                        Was: {consultation.status_before_deletion || consultation.status}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600">{consultation.email} • {consultation.phone}</p>
                    <div className="mt-2 p-2 bg-white rounded border border-red-100">
                      <p className="text-xs text-neutral-500">
                        <strong>Deleted by:</strong> {consultation.deleted_by || 'Unknown'}
                      </p>
                      <p className="text-xs text-neutral-500">
                        <strong>Deleted at:</strong> {consultation.deleted_at ? new Date(consultation.deleted_at).toLocaleString() : 'Unknown'}
                      </p>
                      {consultation.deletion_reason && consultation.deletion_reason !== 'N/A' && (
                        <p className="text-xs text-neutral-500">
                          <strong>Reason:</strong> {consultation.deletion_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-green-200 text-green-700 hover:bg-green-50"
                    onClick={() => handleRestore(consultation.id)}
                    disabled={isRestoring}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${isRestoring ? 'animate-spin' : ''}`} /> Restore
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Confirmation Modal with Installer Selection */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              Confirm Consultation
            </DialogTitle>
          </DialogHeader>
          {selectedConsultation && (
            <div className="space-y-4">
              {/* Customer Info Card */}
              <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-lg">{selectedConsultation.name}</p>
                    <p className="text-sm text-neutral-600 flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" /> {selectedConsultation.email}
                    </p>
                    <p className="text-sm text-neutral-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedConsultation.phone}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium capitalize">
                    {selectedConsultation.project_type}
                  </span>
                </div>
                {selectedConsultation.address && (
                  <p className="text-sm text-neutral-600 mt-2 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> 
                    {selectedConsultation.address}
                  </p>
                )}
                {selectedConsultation.message && (
                  <p className="text-sm text-neutral-500 mt-2 italic bg-white p-2 rounded border">
                    "{selectedConsultation.message}"
                  </p>
                )}
              </div>
              
              {/* Date & Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Appointment Date *</Label>
                  <Input
                    type="date"
                    value={confirmDate}
                    onChange={(e) => setConfirmDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Appointment Time</Label>
                  <select
                    value={confirmTime}
                    onChange={(e) => setConfirmTime(e.target.value)}
                    className="w-full border rounded-md p-2 mt-1 h-10"
                  >
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Installer Assignment */}
              <div>
                <Label className="text-sm font-medium">Assign Installer</Label>
                <select
                  value={selectedInstaller}
                  onChange={(e) => setSelectedInstaller(e.target.value)}
                  className="w-full border rounded-md p-2 mt-1"
                >
                  <option value="">-- Select an installer (optional) --</option>
                  {installers.map(installer => (
                    <option key={installer.id} value={installer.id}>
                      {installer.name} {installer.phone && `• ${installer.phone}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Email Preview */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <p className="text-sm font-medium text-blue-800 flex items-center gap-1">
                  <Mail className="w-4 h-4" /> What happens on confirm:
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3" /> Calendar event added to Schedule
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3" /> Email with ICS calendar invite to customer
                  </li>
                  {selectedInstaller && (
                    <li className="flex items-center gap-2">
                      <Check className="w-3 h-3" /> Email notification to assigned installer
                    </li>
                  )}
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3" /> Admin confirmation email
                  </li>
                </ul>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={isConfirming}>
                  Cancel
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700" 
                  onClick={confirmConsultation}
                  disabled={isConfirming || !confirmDate}
                >
                  {isConfirming ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" /> Confirm Consultation
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Consultation
            </DialogTitle>
          </DialogHeader>
          {deleteConsultationData && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="font-medium text-red-800">You are about to delete:</p>
                <p className="text-red-700 mt-1">{deleteConsultationData.name}</p>
                <p className="text-sm text-red-600">{deleteConsultationData.email}</p>
                <p className="text-sm text-red-600 mt-1">
                  Status: <span className="font-medium capitalize">{deleteConsultationData.status}</span>
                </p>
              </div>
              
              {deleteConsultationData.status === 'completed' && (
                <div>
                  <Label className="text-sm font-medium text-red-700">
                    Reason for deletion (required) *
                  </Label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Please explain why this completed consultation is being deleted..."
                    className="w-full border border-red-200 rounded-md p-3 mt-1 text-sm min-h-[100px] focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>💡 Note:</strong> This is a soft delete. You can restore this consultation from the "Deleted" tab if needed.
                </p>
              </div>
              
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white" 
                  onClick={confirmDelete}
                  disabled={isDeleting || (deleteConsultationData.status === 'completed' && !deleteReason.trim())}
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Modal */}
      <Dialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {selectedIds.length} Consultation(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="font-medium text-red-800">You are about to delete {selectedIds.length} consultation(s):</p>
              <ul className="mt-2 text-sm text-red-700 max-h-32 overflow-y-auto">
                {consultations.filter(c => selectedIds.includes(c.id)).map(c => (
                  <li key={c.id} className="py-1">• {c.name} ({c.status})</li>
                ))}
              </ul>
            </div>
            
            <div>
              <Label className="text-sm font-medium">
                Reason for deletion (optional)
              </Label>
              <textarea
                value={bulkDeleteReason}
                onChange={(e) => setBulkDeleteReason(e.target.value)}
                placeholder="Enter reason for bulk deletion..."
                className="w-full border rounded-md p-3 mt-1 text-sm min-h-[80px]"
              />
            </div>
            
            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>💡 Note:</strong> This is a soft delete. You can restore these consultations from the "Deleted" tab.
              </p>
            </div>
            
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowBulkDeleteModal(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white" 
                onClick={handleBulkDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.length} Item(s)
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Pricing Management Tab


export default ConsultationsTab;
